import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, Component } from "vue";
import { PetitionShiftSearch } from "@/models/petition/PetitionResponse";
import { DateTime } from "luxon";
import { PetitionAttendanceSearchRequest } from "@/models/petition/PetitionRequest";
import { Attendance } from "@/models/transaction/Attendance";
import ChangeWorkResultsApplication from "@/components/petition/form/ChangeWorkResultsApplication.vue";
import OvertimeWorkApplication from "@/components/petition/form/OvertimeWorkApplication.vue";
import VacationApplication from "@/components/petition/form/VacationApplication.vue";
import ChangeShiftApplication from "@/components/petition/form/ChangeShiftApplication.vue";
import { EnumWorkClassification } from "@/models/enum/Enum";
import { useConfirm } from "@/plugins/confirm/confirm";
import { useApi } from "@/plugins/api/api";
import Store from "@/store/index";
import EarlyWorkApplication from "@/components/petition/form/EarlyWorkApplication.vue";

interface FormComponent extends Vue {
  onSubmit(): Promise<boolean>;
}
type Props = {
  value: boolean;
  date: DateTime;
  shiftTable: PetitionShiftSearch;
  weekday: string;
  adminView: boolean;
  employeeId: number;
  workIncode: any;
};
type Emits = {
  (e: "input", val: boolean): void;
  (e: "close"): void;
};
type Item = {
  text: string;
  value: Component;
  icon: string;
  subtitle: string;
  disabled: boolean;
};
type ComponentType =
  | InstanceType<typeof ChangeWorkResultsApplication>
  | InstanceType<typeof OvertimeWorkApplication>
  | InstanceType<typeof VacationApplication>
  | InstanceType<typeof ChangeShiftApplication>
  | InstanceType<typeof EarlyWorkApplication>;

export default /*#__PURE__*/_defineComponent({
  __name: 'PetitionSteppers',
  props: {
    value: { type: Boolean },
    date: null,
    shiftTable: null,
    weekday: null,
    adminView: { type: Boolean },
    employeeId: null,
    workIncode: null
  },
  emits: ["input", "close"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: "input", val: boolean): void;
  (e: "close"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: boolean;
  date: DateTime;
  shiftTable: PetitionShiftSearch;
  weekday: string;
  adminView: boolean;
  employeeId: number;
  workIncode: any;
};

const { $confirm } = useConfirm();
const { Paths, $post } = useApi();

const ApprovalState = {
  NONE: "0",
  DURING: "1",
  RESOLVE: "2",
  REJECT: "3"
} as const;

const isAfd = (shift: PetitionShiftSearch) => {
  return shift.afdFlag === ApprovalState.DURING;
};
const isOfd = (shift: PetitionShiftSearch) => {
  return shift.ofdFlag === ApprovalState.DURING;
};

const isEfdOn = (shift: PetitionShiftSearch) => {
  return shift.efdFlag === ApprovalState.DURING;
};

const isEfd = (shift: PetitionShiftSearch) => {
  return (
    shift.efdFlag === ApprovalState.DURING ||
    shift.shiftPatternId === EnumWorkClassification.OFFICIAL_HOLIDAY ||
    shift.shiftPatternId === EnumWorkClassification.SUBSTITUTE_HOLIDAY ||
    shift.shiftPatternId === EnumWorkClassification.PAID_HOLIDAY ||
    shift.shiftPatternId === EnumWorkClassification.SPECIAL_HOLIDAY ||
    shift.shiftPatternId === EnumWorkClassification.ABSENCE_DAY ||
    shift.shiftPatternId === EnumWorkClassification.OTHER ||
    shift.shiftPatternId === EnumWorkClassification.SICKNESS_HOLIDAY
  );
};

const isApproval = (shift: PetitionShiftSearch) => {
  return isAfd(shift) || isOfd(shift) || isEfdOn(shift);
};
const isVfd = (shift: PetitionShiftSearch) => {
  return (
    isApproval(shift) ||
    shift.shiftPatternId === EnumWorkClassification.OFFICIAL_HOLIDAY ||
    shift.shiftPatternId === EnumWorkClassification.SUBSTITUTE_HOLIDAY ||
    shift.shiftPatternId === EnumWorkClassification.PAID_HOLIDAY ||
    shift.shiftPatternId === EnumWorkClassification.SPECIAL_HOLIDAY ||
    shift.shiftPatternId === EnumWorkClassification.ABSENCE_DAY ||
    shift.shiftPatternId === EnumWorkClassification.OTHER ||
    shift.shiftPatternId === EnumWorkClassification.SICKNESS_HOLIDAY
  );
};




const component = ref<Component>();
const attendance = ref<Attendance>();
const isLoading = ref(false);
const title = "申請";

const modelValue = computed({
  get: () => props.value,
  set: val => emits("input", val)
});

const adminView = computed(() => {
  return props.adminView;
});

const employeeId = computed(() => {
  return props.employeeId;
});

const workIncode = computed(() => {
  return props.workIncode;
});

const items = computed((): Item[] => {
  if (adminView.value == true) {
    if (isEfd(props.shiftTable)) {
      return [];
    } else {
      component.value = EarlyWorkApplication;
      return [
        {
          text: "早出申請",
          value: EarlyWorkApplication,
          icon: "mdi-autorenew",
          subtitle: "予定の時間よりも前からの勤務の場合",
          disabled: isEfd(props.shiftTable)
        }
      ];
    }
  } else {
    return [
      {
        text: "シフト申請",
        value: ChangeShiftApplication,
        icon: "mdi-table-edit ",
        subtitle: "日程の変更、就業予定の変更を行う場合",
        disabled: isApproval(props.shiftTable)
      },
      {
        text: "休暇申請",
        value: VacationApplication,
        icon: "mdi-bag-suitcase",
        subtitle: "有給休暇・欠勤・振替などの申請を行う場合",
        disabled: isVfd(props.shiftTable)
      },
      {
        text: "残業申請",
        value: OvertimeWorkApplication,
        icon: "mdi-briefcase-clock",
        subtitle: "予定の時間を超えて勤務を行う場合",
        disabled: isOfd(props.shiftTable)
      },
      {
        text: "勤務時間変更依頼",
        value: ChangeWorkResultsApplication,
        icon: "mdi-autorenew",
        subtitle: "勤務時間帯の変更、早出、休憩時間の変更の場合",
        disabled: isAfd(props.shiftTable)
      },
      {
        text: "早出申請",
        value: EarlyWorkApplication,
        icon: "mdi-autorenew",
        subtitle: "予定の時間よりも前からの勤務の場合",
        disabled: isEfd(props.shiftTable)
      }
    ];
  }
});

const componentRef = ref<ComponentType>();

const onClose = async () => {
  const message = "申請処理が完了していません。\n戻りますか？";
  const confirm = await $confirm(message);
  if (confirm) {
    modelValue.value = false;
    component.value = undefined;
  }
};
const onBack = async () => {
  if (component.value) {
    component.value = undefined;
  } else {
    onClose();
  }
};
const onSubmit = async () => {
  isLoading.value = true;
  const result = await componentRef.value?.onSubmit();
  if (result) {
    modelValue.value = false;
    component.value = undefined;
    emits("close");
  }
  isLoading.value = false;
};

const onListClick = (item: Item) => {
  component.value = item.value;
};

watch(
  () => props.date,
  async val => {
    const date = val.toISODate();
    if (date == null) return;
    const request = new PetitionAttendanceSearchRequest({
      affiliationId: Store.state.workIncode ?? workIncode.value,
      employeeId: employeeId.value,
      attendanceDate: date
    });
    const result = await $post<Attendance[]>(
      Paths.petition.search.attendance,
      request
    );
    if (result && result.length !== 0) {
      attendance.value = result[0];
    } else {
      attendance.value = undefined;
    }
  }
);

return { __sfc: true,$confirm, Paths, $post, ApprovalState, isAfd, isOfd, isEfdOn, isEfd, isApproval, isVfd, props, emits, component, attendance, isLoading, title, modelValue, adminView, employeeId, workIncode, items, componentRef, onClose, onBack, onSubmit, onListClick, Store }
}

})